/**
 * Why a **Lieferung** waits although it stands in the queue (TM-9636 / #1621).
 *
 * ⚠ **An observation at production time, not a frozen fact.** Both reasons can
 * be gone tomorrow: the Kostenträgerdatei is republished 15 to 25 times a year,
 * and the recipient-key directory lies in memory and is empty after a restart
 * until 06:00 on the next business day. The dispatch step therefore checks
 * again itself; what stands here is the information for the Versand operator
 * about why this Lieferung is left lying.
 *
 * ⚠ **The practice sees none of them.** To it, a waiting Lieferung is called
 * *"Wartet auf Versand"* and nothing else — none of these reasons is something
 * a practice could judge or remedy.
 *
 * ⚠ **Never a fallback to Übergabe.** A blocked Lieferung waits; it is not
 * handed to the practice as a file pair instead. Diverting to Übergabe is a
 * human's decision and belongs to TM-9637.
 */
export var LieferungBlocker;
(function (LieferungBlocker) {
    /**
     * The edition in force does not describe the physical recipient at all —
     * there is no `UNH…UNT` message for its IK.
     */
    LieferungBlocker["DISPATCH_ADDRESS_MISSING"] = "DISPATCH_ADDRESS_MISSING";
    /**
     * It is described, but publishes no `DFU 070` under a
     * Datenfernübertragung medium. Falling back to `016` (FTAM) or the X.400
     * remnants would be a value that is not an address in an SMTP `To:`.
     */
    LieferungBlocker["DISPATCH_ADDRESS_NO_EMAIL_CHANNEL"] = "DISPATCH_ADDRESS_NO_EMAIL_CHANNEL";
    /**
     * It publishes more than one `070`, and they contradict each other. Anhang 3
     * names no rule for the choice, so a human decides and not the order of the
     * lines in the file.
     */
    LieferungBlocker["DISPATCH_ADDRESS_AMBIGUOUS"] = "DISPATCH_ADDRESS_AMBIGUOUS";
    /**
     * There is no valid certificate for the encryption target (`VKG 03`) in the
     * recipient-key directory.
     */
    LieferungBlocker["RECIPIENT_KEY_MISSING"] = "RECIPIENT_KEY_MISSING";
    /**
     * There is no active **Absenderzertifikat** to sign with (TM-9636 / #1622).
     *
     * ⚠ **Not the same as a missing recipient key.** That one affects one
     * Datenannahmestelle, this one affects **every** Lieferung of every practice:
     * it is the one certificate the whole Versand hangs on (`CONTEXT.md`,
     * *Absenderzertifikat*). Outside production there is the fake for it; in
     * production this reason means the environment is not provisioned for
     * Versand.
     */
    LieferungBlocker["ABSENDERZERTIFIKAT_MISSING"] = "ABSENDERZERTIFIKAT_MISSING";
    /**
     * No dispatch path is set up in this environment (TM-9636 / #1622).
     *
     * ⚠ **A waiting reason and not a startup error.** Without Postfix the backend
     * keeps running and every Lieferung waits — the same rule as with the
     * Absenderzertifikat's `GKV_VERSAND_PROVISIONED` switch. A service that would
     * not come up over a missing dispatch path would take a whole installation
     * out of operation for a billing that does not exist there yet.
     */
    LieferungBlocker["TRANSPORT_MISSING"] = "TRANSPORT_MISSING";
})(LieferungBlocker || (LieferungBlocker = {}));
//# sourceMappingURL=lieferung-blocker.enum.js.map